// A water-filled tube that slides out of a pinch. The pinch stays under the
// finger while the material slides through it, so each vertex samples the cage
// at its shifted position (the point now under the finger), not at its own rest
// point. Floaters inside lag behind on springs. After a short slide the tube
// escapes the grip entirely and springs back to the middle. The gain, the
// escape distance and the soft return are design values, not fluid measurements.
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const coordinates = new Float64Array(3), middle = new Int32Array(3), weights = new Float64Array(9);

// Allocation-free quadratic B-spline sample of a cage field at an arbitrary point.
export function sampleField(field, point, out) {
  const n = field.n;
  for (let axis = 0; axis < 3; axis++) {
    coordinates[axis] = (point[axis] - field.min[axis]) / (field.max[axis] - field.min[axis]) * (n[axis] - 1);
    middle[axis] = Math.floor(coordinates[axis] + .5); out[axis] = point[axis];
    for (let j = -1; j <= 1; j++) { const d = Math.abs(coordinates[axis] - middle[axis] - j); weights[axis * 3 + j + 1] = d < .5 ? .75 - d * d : .5 * (1.5 - d) ** 2; }
  }
  for (let z = -1; z <= 1; z++) for (let y = -1; y <= 1; y++) for (let x = -1; x <= 1; x++) {
    const index = (clamp(middle[0] + x, 0, n[0] - 1) + n[0] * (clamp(middle[1] + y, 0, n[1] - 1) + n[1] * clamp(middle[2] + z, 0, n[2] - 1))) * 3;
    const weight = weights[x + 1] * weights[3 + y + 1] * weights[6 + z + 1];
    for (let axis = 0; axis < 3; axis++) out[axis] += (field.p[index + axis] - field.initialRest[index + axis]) * weight;
  }
  return out;
}

export class SlipTube {
  constructor({ length = 2, axis = 0, gain = 4, limit = .34, escape = .28, floaters = 0, center = [0, 0], radius = .3 } = {}) {
    this.length = length; this.axis = axis; this.gain = gain; this.center = [...center]; this.radius = radius;
    this.waist = 0; this.waistWidth = radius * 1.1;
    this.limit = limit * length; this.escape = escape * length;
    this.shift = 0; this.velocity = 0; this.pinch = 0; this.pinchAt = 0; this.held = false; this.grabShift = 0;
    this.activity = 0; this.escapedNow = false; this.escapes = 0;
    this.floaters = Array.from({ length: floaters }, (_, i) => ({ offset: 0, velocity: 0, stiffness: 34 + i * 9 }));
  }
  // A hand pinches the tube at a toy-local position along the axis, depth 0..1.
  hold(position, depth) {
    if (!Number.isFinite(position) || !Number.isFinite(depth)) return;
    if (!this.held) { this.held = true; this.grabShift = this.shift; }
    this.pinchAt = position; this.pinch = clamp(depth, 0, 1);
  }
  release() { this.held = false; this.pinch = 0; }
  reset() {
    this.shift = 0; this.velocity = 0; this.pinch = 0; this.held = false; this.activity = 0; this.escapedNow = false; this.escapes = 0; this.waist = 0;
    for (const f of this.floaters) { f.offset = 0; f.velocity = 0; }
  }
  step(dt) {
    this.escapedNow = false;
    if (!Number.isFinite(dt) || dt <= 0) return;
    dt = Math.min(dt, 1 / 30);
    const steps = Math.max(1, Math.ceil(dt / (1 / 120))), h = dt / steps, previousWaist = this.waist;
    for (let s = 0; s < steps; s++) {
      const waistGoal = this.held ? this.pinch : 0;
      this.waist += (waistGoal - this.waist) * (1 - Math.exp(-h / (waistGoal > this.waist ? .05 : .2)));
      if (this.held && this.pinch > .02) {
        // The material under the pinch, measured from the tube centre. The
        // tube squirts toward the longer side and slips faster near an end.
        const material = this.pinchAt - this.shift, direction = material > 0 ? -1 : 1;
        const lever = clamp(Math.abs(material) / (this.length * .5), .15, 1);
        this.velocity += direction * this.gain * this.pinch * lever * this.length * h;
        this.velocity *= Math.exp(-2.5 * h);
        if (Math.abs(this.shift - this.grabShift) > this.escape || Math.abs(material) > this.length * .46) {
          this.held = false; this.pinch = 0; this.escapedNow = true; this.escapes++; this.velocity *= 1.5;
        }
      } else {
        // Nothing holds it: a soft spring brings the tube back to the middle.
        this.velocity += (-this.shift * 14 - this.velocity * 4.2) * h;
      }
      this.shift += this.velocity * h;
      if (Math.abs(this.shift) > this.limit) { this.shift = Math.sign(this.shift) * this.limit; this.velocity *= -.3; }
      for (const f of this.floaters) {
        f.velocity += ((this.shift - f.offset) * f.stiffness - f.velocity * 6.5) * h;
        f.offset += f.velocity * h;
      }
    }
    if (!this.held && this.waist < .002) this.waist = 0;
    let activity = Math.abs(this.velocity) + (this.held ? .001 : 0) + Math.abs(this.waist - previousWaist) / dt + (this.waist > 0 ? .001 : 0);
    for (const f of this.floaters) activity += Math.abs(f.velocity) + Math.abs(f.offset - this.shift);
    if (!this.held && this.waist === 0 && Math.abs(this.shift) < .0008 && activity < .004) {
      this.shift = 0; this.velocity = 0; activity = 0;
      for (const f of this.floaters) { f.offset = 0; f.velocity = 0; }
    }
    this.activity = activity;
  }
  // Rest point → posed point: slide along the axis, then dent by the cage at the slid position.
  deform(field, rest, out, scratch) {
    scratch[0] = rest[0]; scratch[1] = rest[1]; scratch[2] = rest[2];
    scratch[this.axis] += this.shift;
    sampleField(field, scratch, out);
    if (this.waist > 0) {
      // Radial squeeze around the axis at the finger, with small bulges either
      // side where the pinched water goes. Area is roughly kept, not exactly.
      const w = this.waistWidth, d = scratch[this.axis] - this.pinchAt;
      const g = x => Math.exp(-(x / w) * (x / w));
      const s = 1 - .52 * this.waist * g(d) + .16 * this.waist * (g(d - 1.8 * w) + g(d + 1.8 * w));
      for (let a = 0, c = 0; a < 3; a++) {
        if (a === this.axis) continue;
        out[a] = this.center[c] + (out[a] - this.center[c]) * s; c++;
      }
    }
    return out;
  }
}
